/**
 * certExpiry — classificação pura de proximidade de vencimento do certificado A1.
 *
 * O certificado ICP-Brasil (A1) vence em ~1 ano. Emitir com cert vencido é
 * rejeição certa no fisco, então o produto precisa avisar ANTES. Esta função é
 * pura (sem IO/Date.now implícito além do `now` injetável) para ser exercível
 * por teste — mesma disciplina do productionGate.
 *
 * Faixas (dias até `not_after`):
 *   expired  → já venceu (not_after <= now)
 *   urgent   → vence em <= 7 dias
 *   warn     → vence em <= 30 dias
 *   ok       → vence em > 30 dias
 *   none     → sem certificado / sem data válida (nada a alertar)
 */

export type CertExpiryLevel = 'none' | 'ok' | 'warn' | 'urgent' | 'expired';

export interface CertExpiryStatus {
  level: CertExpiryLevel;
  /** Dias inteiros até o vencimento (negativo/zero quando vencido); null se sem data. */
  days: number | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export const CERT_EXPIRY_WARN_DAYS = 30;
export const CERT_EXPIRY_URGENT_DAYS = 7;

/**
 * Classifica o vencimento de um certificado.
 * @param notAfter data de expiração (ISO string, Date, ou null/undefined)
 * @param now      "agora" injetável (default: relógio real) — mantém a função testável
 */
export function certExpiryStatus(
  notAfter: string | Date | null | undefined,
  now: Date = new Date(),
): CertExpiryStatus {
  if (notAfter == null) return { level: 'none', days: null };
  const exp = notAfter instanceof Date ? notAfter : new Date(notAfter);
  if (Number.isNaN(exp.getTime())) return { level: 'none', days: null };

  const diffMs = exp.getTime() - now.getTime();
  const days = Math.floor(diffMs / DAY_MS);

  if (diffMs <= 0) return { level: 'expired', days };
  if (days <= CERT_EXPIRY_URGENT_DAYS) return { level: 'urgent', days };
  if (days <= CERT_EXPIRY_WARN_DAYS) return { level: 'warn', days };
  return { level: 'ok', days };
}

/** Conveniência p/ UI: precisa mostrar aviso (warn/urgent/expired). */
export function certExpiryNeedsAttention(level: CertExpiryLevel): boolean {
  return level === 'warn' || level === 'urgent' || level === 'expired';
}
